/**
 * Snusify coverflow.
 *
 * Liquid renders every card; this script only tells each one where it sits
 * relative to the active card, as data-pos 0, ±1, ±2 or far-l/far-r, and the
 * stylesheet turns that into offset, scale and scrim.
 *
 * Below 768px the stage is a native scroll-snap row instead. There the script
 * stops positioning cards and follows the scroll, so the dots stay in step
 * with whichever card the visitor has swiped to the centre.
 *
 * It turns by itself every AUTOPLAY_MS while it is on screen, and holds
 * while pointed at, touched or focused, while the tab is hidden, and after
 * the pause button (WCAG 2.2.2). Any turn the visitor makes starts the wait
 * over. Not for anyone who prefers less motion, and not in the editor.
 */

const MOBILE = window.matchMedia('(max-width: 767px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** How long each card stays at the front before the next comes round. */
const AUTOPLAY_MS = 4000;

class SnusifyGallery extends HTMLElement {
  #stage = null;
  #cards = [];
  #dots = [];
  #live = null;
  #active = 0;
  #programmatic = false;
  #raf = 0;
  #pauseButton = null;
  #timer = 0;
  #inView = false;
  #held = false;
  #paused = false;
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused, and listeners bound to it would silently never fire. */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#stage = this.querySelector('[data-gallery-stage]');
    if (!this.#stage) return;

    this.#cards = [...this.#stage.querySelectorAll('.snusify-cf')];
    this.#dots = [...this.querySelectorAll('[data-gallery-dot]')];
    this.#live = this.querySelector('[data-gallery-live]');
    this.#pauseButton = this.querySelector('[data-gallery-pause]');
    if (this.#cards.length === 0) return;

    const start = Number.parseInt(this.dataset.start ?? '0', 10);
    this.#active = Math.min(Math.max(start, 0), this.#cards.length - 1);

    this.#bind();
    this.#paint();
    if (MOBILE.matches) {
      this.#scrollToActive(false);
      this.#shape();
    }
    this.#initAutoplay();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#raf);
    clearTimeout(this.#timer);
  }

  #initAutoplay() {
    if (REDUCED_MOTION.matches || window.Shopify?.designMode || this.#cards.length < 2) return;
    const { signal } = this.#abort;
    const hold = (held) => {
      this.#held = held;
      this.#schedule();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        this.#inView = entries.some((entry) => entry.isIntersecting);
        this.#schedule();
      },
      { threshold: 0.3 }
    );
    observer.observe(this.#stage);
    signal.addEventListener('abort', () => observer.disconnect());

    this.addEventListener('pointerenter', () => hold(true), { signal });
    this.addEventListener('pointerleave', () => hold(false), { signal });
    this.addEventListener('focusin', () => hold(true), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) hold(false);
      },
      { signal }
    );
    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });

    const button = this.#pauseButton;
    if (button instanceof HTMLElement) {
      button.hidden = false;
      button.addEventListener(
        'click',
        () => {
          this.#paused = !this.#paused;
          button.setAttribute('aria-pressed', String(this.#paused));
          this.#schedule();
        },
        { signal }
      );
    }
  }

  /** Starts the wait for the next turn over, or stops it while held. */
  #schedule() {
    clearTimeout(this.#timer);
    if (!this.#inView || this.#held || this.#paused || document.hidden) return;
    this.#timer = setTimeout(() => {
      // Its own turn: the live region stays quiet, it is not the visitor's.
      this.#active = (this.#active + 1) % this.#cards.length;
      this.#paint();
      if (MOBILE.matches) this.#scrollToActive(true);
      this.#schedule();
    }, AUTOPLAY_MS);
  }

  #bind() {
    const { signal } = this.#abort;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.setActive(this.#active - 1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.setActive(this.#active + 1), { signal });

    for (const [i, dot] of this.#dots.entries()) {
      dot.addEventListener('click', () => this.setActive(i), { signal });
    }

    // A side card brings itself to the front. The front card opens its
    // product wherever it is clicked, except on its own controls.
    this.#stage.addEventListener(
      'click',
      (event) => {
        const card = event.target.closest('.snusify-cf');
        if (!card) return;
        const i = this.#cards.indexOf(card);
        if (i !== this.#active) {
          if (event.target.closest('[data-gallery-pick]')) this.setActive(i);
          return;
        }
        if (event.target.closest('a[href], input, label, select, textarea, button:not([data-gallery-pick])')) return;
        const link = /** @type {HTMLAnchorElement | null} */ (card.querySelector('.snusify-cf__name'));
        if (!link) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey) window.open(link.href, '_blank', 'noopener');
        else window.location.assign(link.href);
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (event) => {
        if (!event.target.closest('[data-gallery-prev], [data-gallery-next], [data-gallery-dot]')) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.setActive(this.#active - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.setActive(this.#active + 1);
        }
      },
      { signal }
    );

    this.#stage.addEventListener('scroll', () => this.#followScroll(), { passive: true, signal });
    // Once more where the scroll comes to rest, in case its last frame drew
    // nothing, so the cards always settle exactly.
    this.#stage.addEventListener(
      'scrollend',
      () => {
        if (MOBILE.matches) this.#shape();
      },
      { signal }
    );
    MOBILE.addEventListener(
      'change',
      () => {
        this.#paint();
        if (MOBILE.matches) {
          this.#scrollToActive(false);
          this.#shape();
        }
      },
      { signal }
    );

    // A card's width follows the screen's, and with it the steps; the page
    // may still be settling when the first shape is taken.
    const reshape = () => {
      if (MOBILE.matches) this.#shape();
    };
    const resize = new ResizeObserver(reshape);
    resize.observe(this.#stage);
    signal.addEventListener('abort', () => resize.disconnect());
    window.addEventListener('load', reshape, { once: true, signal });
    document.fonts?.ready.then(reshape);
  }

  /**
   * @param {number} i - target card; wraps in both directions
   * @param {{ fromScroll?: boolean }} [options]
   */
  setActive(i, options = {}) {
    const count = this.#cards.length;
    this.#active = ((i % count) + count) % count;
    this.#paint();
    this.#announce();
    if (MOBILE.matches && !options.fromScroll) this.#scrollToActive(true);
    this.#schedule();
  }

  #paint() {
    const count = this.#cards.length;

    for (const [i, card] of this.#cards.entries()) {
      // Signed distance from the active card, wrapped to the nearer side.
      let d = (((i - this.#active) % count) + count) % count;
      if (d > count / 2) d -= count;

      let pos = String(d);
      if (Math.abs(d) > 2) pos = d > 0 ? 'far-r' : 'far-l';

      card.dataset.pos = pos;
      card.classList.toggle('is-active', d === 0);

      // The front card's artwork opens the product like the rest of the card,
      // so its bring-to-front button steps out of the way; the name link is
      // the keyboard's way in.
      const pick = card.querySelector('[data-gallery-pick]');
      if (pick instanceof HTMLElement) {
        pick.tabIndex = d === 0 ? -1 : 0;
        if (d === 0) pick.setAttribute('aria-hidden', 'true');
        else pick.removeAttribute('aria-hidden');
      }

      // Off-stage cards are invisible; keep them out of the tab order too.
      const offStage = pos.startsWith('far') && !MOBILE.matches;
      card.inert = offStage;
    }

    for (const [i, dot] of this.#dots.entries()) {
      const on = i === this.#active;
      dot.classList.toggle('is-active', on);
      if (on) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }

  #announce() {
    if (!this.#live) return;
    const name = this.#cards[this.#active].querySelector('.snusify-cf__name')?.textContent.trim() ?? '';
    this.#live.textContent = '';
    requestAnimationFrame(() => {
      this.#live.textContent = name;
    });
  }

  #scrollToActive(smooth) {
    const card = this.#cards[this.#active];
    if (!card) return;
    const left = card.offsetLeft - (this.#stage.clientWidth - card.offsetWidth) / 2;
    this.#programmatic = true;
    this.#stage.scrollTo({ left, behavior: smooth && !REDUCED_MOTION.matches ? 'smooth' : 'auto' });
    setTimeout(() => {
      this.#programmatic = false;
    }, 500);
  }

  /**
   * Phones: shapes every card by its distance from the middle of the row, in
   * card steps - full size at the middle, 84% and dimmed a step away, the
   * nearer ones on top - for the stylesheet's phone rules to apply. Scale
   * and scrim only, so where each card snaps never moves.
   */
  #shape() {
    const [first, second] = this.#cards;
    if (!first) return;
    const step = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
    if (step <= 0) return;
    const centre = this.#stage.scrollLeft + this.#stage.clientWidth / 2;
    for (const card of this.#cards) {
      const d = (card.offsetLeft + card.offsetWidth / 2 - centre) / step;
      const near = Math.min(Math.abs(d), 1);
      card.style.setProperty('--snusify-m-s', (1 - 0.16 * near).toFixed(4));
      card.style.setProperty('--snusify-m-dim', (0.45 * near).toFixed(3));
      // 5 at the front down to 1, as on the coverflow, so the arrows (10) stay over every card.
      card.style.setProperty('--snusify-m-z', String(Math.round(5 - Math.min(Math.abs(d), 2) * 2)));
    }
  }

  #followScroll() {
    if (!MOBILE.matches) return;
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      this.#shape();
      if (this.#programmatic) return;
      const centre = this.#stage.scrollLeft + this.#stage.clientWidth / 2;
      let best = 0;
      let bestDistance = Infinity;
      for (const [i, card] of this.#cards.entries()) {
        const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - centre);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      if (best !== this.#active) this.setActive(best, { fromScroll: true });
    });
  }
}

if (!customElements.get('snusify-gallery')) {
  customElements.define('snusify-gallery', SnusifyGallery);
}
